import React, { useState, useEffect, useRef } from 'react'
import { Button } from 'semantic-ui-react'
import { admin, api } from 'utils/actions'
import { sanitizeHtml } from 'utils/sanitizeHtml'
import { uploadRaw } from 'utils/cloudinary'

const post = (qs, body) =>
  fetch(admin + qs, {
    method: 'post',
    headers: {
      Authorization: localStorage.getItem('token'),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  }).then(r => r.json())

const readDataUrl = file =>
  new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result)
    fr.onerror = reject
    fr.readAsDataURL(file)
  })

const exec = (cmd, val) => document.execCommand(cmd, false, val)

const COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Gray', value: '#767676' },
  { name: 'Red', value: '#db2828' },
  { name: 'Orange', value: '#f2711c' },
  { name: 'Yellow', value: '#fbbd08' },
  { name: 'Green', value: '#21ba45' },
  { name: 'Teal', value: '#00b5ad' },
  { name: 'Blue', value: '#2185d0' },
  { name: 'Purple', value: '#6435c9' },
  { name: 'Pink', value: '#e03997' },
  { name: 'Brown', value: '#a5673f' },
  { name: 'White', value: '#ffffff' },
]

const News = () => {
  const editor = useRef(null)
  const imgInput = useRef(null)
  const fileInput = useRef(null)
  const savedRange = useRef(null)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [linking, setLinking] = useState(false)

  useEffect(() => {
    // Emit inline CSS (e.g. <span style="color:..">) rather than deprecated tags.
    try {
      document.execCommand('styleWithCSS', false, true)
    } catch (e) {
      /* ignore */
    }
    fetch(api + 'lookup=1')
      .then(r => r.json())
      .then(d => {
        if (editor.current) editor.current.innerHTML = (d && d.news) || ''
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [])

  // Remember the last selection inside the editor so toolbar controls that
  // steal focus (color picker, prompts, file dialog) can restore it.
  const saveSel = () => {
    const sel = window.getSelection()
    if (
      sel &&
      sel.rangeCount &&
      editor.current &&
      editor.current.contains(sel.anchorNode)
    )
      savedRange.current = sel.getRangeAt(0)
  }

  const withSelection = fn => {
    editor.current && editor.current.focus()
    const sel = window.getSelection()
    if (savedRange.current) {
      sel.removeAllRanges()
      sel.addRange(savedRange.current)
    }
    fn()
  }

  // Buttons use onMouseDown + preventDefault so the editor keeps its selection.
  const cmd = (command, value) => e => {
    e.preventDefault()
    withSelection(() => exec(command, value))
  }

  const hasSelection = () =>
    savedRange.current && !savedRange.current.collapsed

  // Link the selected text to a file: pick it, upload to Cloudinary, link it.
  const addLink = e => {
    e.preventDefault()
    saveSel()
    if (!hasSelection()) return alert('Select the text to link first.')
    fileInput.current && fileInput.current.click()
  }

  const onPickFile = async e => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    setLinking(true)
    const url = await uploadRaw(file, 'news').catch(() => null)
    setLinking(false)
    if (url) withSelection(() => exec('createLink', url))
    else alert('File upload failed.')
  }

  const onPickImage = async e => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    const url = await readDataUrl(file)
    const res = await post('cdupload=1', {
      url,
      folder: 'news',
      name: Date.now(),
    })
    const src = res && (res.secure_url || res.url)
    if (src) withSelection(() => exec('insertImage', src))
    else alert('Image upload failed.')
  }

  const save = async () => {
    setSaving(true)
    const clean = sanitizeHtml(editor.current.innerHTML)
    editor.current.innerHTML = clean // reflect the sanitized result in the editor
    await post('setnews=1', { news: clean })
    setSaving(false)
    alert('News saved.')
  }

  const Tool = props => <Button size="small" basic type="button" {...props} />

  return (
    <div>
      <h3>News</h3>
      <div style={{ color: '#888', marginBottom: 8 }}>
        Edits the News section shown at the top of the home page.
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          alignItems: 'center',
        }}
      >
        <Tool onMouseDown={cmd('formatBlock', 'H1')}>H1</Tool>
        <Tool onMouseDown={cmd('formatBlock', 'H2')}>H2</Tool>
        <Tool onMouseDown={cmd('formatBlock', 'P')}>P</Tool>
        <Tool onMouseDown={cmd('bold')}>
          <b>B</b>
        </Tool>
        <Tool onMouseDown={cmd('italic')}>
          <i>I</i>
        </Tool>
        <Tool onMouseDown={cmd('insertUnorderedList')} icon="list ul" />
        <Tool
          onMouseDown={addLink}
          icon="linkify"
          title="Link selected text to a file"
          loading={linking}
          disabled={linking}
        />
        <Tool
          onMouseDown={e => {
            e.preventDefault()
            saveSel()
            imgInput.current && imgInput.current.click()
          }}
          icon="image"
        />
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            marginLeft: 4,
          }}
        >
          <span style={{ marginRight: 2 }}>Color:</span>
          {COLORS.map(c => (
            <span
              key={c.value}
              title={c.name}
              onMouseDown={cmd('foreColor', c.value)}
              style={{
                width: 20,
                height: 20,
                display: 'inline-block',
                background: c.value,
                border: '1px solid #ccc',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            />
          ))}
        </span>
        <input
          ref={imgInput}
          type="file"
          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
          hidden
          onChange={onPickImage}
        />
        <input ref={fileInput} type="file" hidden onChange={onPickFile} />
      </div>

      <div
        ref={editor}
        contentEditable
        suppressContentEditableWarning
        onKeyUp={saveSel}
        onMouseUp={saveSel}
        onBlur={saveSel}
        className="mt8"
        style={{
          border: '1px solid #ccc',
          borderRadius: 4,
          minHeight: 320,
          padding: 12,
          background: 'white',
        }}
      />

      <div className="mt16">
        <Button primary onClick={save} loading={saving} disabled={saving || !loaded}>
          Save
        </Button>
      </div>
    </div>
  )
}

export default News
